import {
  ProcessFlowchartNodeDetailsType,
  ProcessTaskStatusType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import type {
  EndStaticNodeData,
  RouterStaticNodeData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';

import {ProcessFlowchartStructureQuery} from './__generated/ProcessFlowchartStructureQuery';
import {ProcessTaskGroupFlowchartDataQuery} from './__generated/ProcessTaskGroupFlowchartDataQuery';
import type {
  TaskSchemaViewerNodeData,
  ViewerStaticFlowchartNodeData,
  ViewerStaticTemplateFlowchart,
} from './ReadOnlyFlowchartTypes';

type FlowchartStructure = ExtractByTypename<
  ProcessFlowchartStructureQuery['processFlowcharts'][number],
  'ProcessFlowchartNode'
>;

type NodeConfig = FlowchartStructure['nodeConfigs'][number];

type FlowchartProcess = ExtractByTypename<ProcessTaskGroupFlowchartDataQuery['process'], 'ProcessNode'>;

export interface DisplayOnlyFlowchartResult {
  flowchart: ViewerStaticTemplateFlowchart;
  processIdsByNodeConfigId: Record<string, string>;
}

function buildTaskSchemaNode(
  nodeConfig: NodeConfig,
  nodeDetailsType: ProcessFlowchartNodeDetailsType,
  schemaId: string,
  schemaName: string,
  statusCounts?: Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
): TaskSchemaViewerNodeData {
  return {
    id: nodeConfig.id,
    nodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    nodeDetailsType,
    nodeName: nodeConfig.name || schemaName,
    taskSchemaId: schemaId,
    statusCounts,
  };
}

function buildNodeFromConfig(
  nodeConfig: NodeConfig,
  statusCountsByNodeConfigId: Record<
    string,
    Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
  >
): ViewerStaticFlowchartNodeData | null {
  const {nodeDetails} = nodeConfig;
  const statusCounts = statusCountsByNodeConfigId[nodeConfig.id];
  switch (nodeDetails.__typename) {
    case 'ProcessRootNodeDetailsNode': {
      const schemaId = !isInaccessible(nodeDetails.taskSchema) ? nodeDetails.taskSchema.id : 'unknown';
      const schemaName = !isInaccessible(nodeDetails.taskSchema) ? nodeDetails.taskSchema.name : 'Start';
      return buildTaskSchemaNode(nodeConfig, nodeDetails.type, schemaId, schemaName, statusCounts);
    }
    case 'ProcessTaskNodeDetailsNode': {
      const schemaId = !isInaccessible(nodeDetails.schema) ? nodeDetails.schema.id : 'unknown';
      const schemaName = !isInaccessible(nodeDetails.schema) ? nodeDetails.schema.name : 'Task';
      return buildTaskSchemaNode(nodeConfig, nodeDetails.type, schemaId, schemaName, statusCounts);
    }
    case 'ProcessOutputNodeDetailsNode':
      return buildTaskSchemaNode(
        nodeConfig,
        nodeDetails.type,
        `output_${nodeConfig.id}`,
        'Output',
        statusCounts
      );
    case 'ProcessRouterNodeDetailsNode':
      return {
        id: nodeConfig.id,
        nodeType: ProcessFlowchartNodeType.ROUTER_NODE,
        nodeDetailsType: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
        nodeName: nodeDetails.name,
        routerConfig: {
          clientId: nodeConfig.id,
          name: nodeDetails.name,
          functionSpecs: [],
          defaultFunctionSpec: null,
        },
      } satisfies RouterStaticNodeData;
    case 'ProcessEndNodeDetailsNode':
      return {
        id: nodeConfig.id,
        nodeType: ProcessFlowchartNodeType.STOP_NODE,
        nodeDetailsType: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
        nodeName: nodeDetails.name,
      } as EndStaticNodeData;
    default:
      return null;
  }
}

function buildEdgeFromConfig(edgeConfig: FlowchartStructure['edgeConfigs'][number]): FlowchartStaticEdgeData {
  const sourceId = edgeConfig.fromNodeConfig.id;
  const targetId = edgeConfig.toNodeConfig.id;
  return {
    id: edgeConfig.id,
    source: sourceId,
    target: targetId,
    sourceHandle: `${sourceId}-source`,
    targetHandle: `${targetId}-target`,
    type: ProcessFlowchartEdgeType.CONTROL_FLOW,
  };
}

function buildLineageMaps(flowchartProcess: FlowchartProcess): {
  statusCountsByNodeConfigId: Record<
    string,
    Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
  >;
  processIdsByNodeConfigId: Record<string, string>;
} {
  const statusCountsByNodeConfigId: Record<
    string,
    Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
  > = {};
  const processIdsByNodeConfigId: Record<string, string> = {};

  const allProcesses = [flowchartProcess, ...flowchartProcess.childProcesses];
  for (const process of allProcesses) {
    if (isInaccessible(process) || process.nodeConfig == null) {
      continue;
    }
    if (process.taskStatusCounts.length === 0) {
      continue;
    }
    statusCountsByNodeConfigId[process.nodeConfig.id] = process.taskStatusCounts.map((sc) => ({
      statusType: sc.status.statusType,
      displayName: sc.status.displayName,
      count: sc.count,
    }));
    processIdsByNodeConfigId[process.nodeConfig.id] = process.id;
  }

  return {statusCountsByNodeConfigId, processIdsByNodeConfigId};
}

/**
 * Builds a lightweight, read-only flowchart from the GraphQL structure query.
 * Used by the execution viewer to render the flowchart without loading the full
 * config editor machinery. Optionally merges status counts from lineage data.
 */
export default function buildDisplayOnlyDraftFromStructure(
  flowchartStructure: FlowchartStructure,
  flowchartProcess?: FlowchartProcess
): DisplayOnlyFlowchartResult {
  const {statusCountsByNodeConfigId, processIdsByNodeConfigId} =
    flowchartProcess != null
      ? buildLineageMaps(flowchartProcess)
      : {statusCountsByNodeConfigId: {}, processIdsByNodeConfigId: {}};

  const nodesStaticData: Array<ViewerStaticFlowchartNodeData> = [];
  for (const nodeConfig of flowchartStructure.nodeConfigs) {
    const node = buildNodeFromConfig(nodeConfig, statusCountsByNodeConfigId);
    if (node != null) {
      nodesStaticData.push(node);
    }
  }

  const edgesStaticData: Array<FlowchartStaticEdgeData> = flowchartStructure.edgeConfigs.map((edge) =>
    buildEdgeFromConfig(edge)
  );

  return {
    flowchart: {nodesStaticData, edgesStaticData},
    processIdsByNodeConfigId,
  };
}
